import { epochSecondsToDate, z, type ZodType } from '@customerio/hydra-validation';
import type { Export as ApiExport } from '@customerio/hydra-ui-api';
import { generatePath } from '@/services/router/generate-path';
import { defineEntity } from '../shared/define-entity';

/**
 * Export entity — workspace-scoped data exports (CSV exports of People /
 * Campaign / Broadcast data). Read-only from the UI; the only write is the
 * signed-download action (see `queries.ts`).
 *
 * Migrated from the Ember `export` model: `id` is coerced to `string` per the
 * layer convention, `created_at`/`updated_at` become `Date`, and the model's
 * computed getters (progress, completeness, "exported by", and the deep link
 * back to the source resource) are ported as the plain functions below.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

export type Export = Omit<ApiExport, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

// The export options blob is loosely typed on the backend; the fields we read
// for link-building aren't all in the generated schema, so widen it locally.
type ExportOptions = NonNullable<Export['options']> & {
  campaign?: number;
  campaign_ids?: number[];
  newsletter?: number;
  newsletter_ids?: number[];
  segment_id?: number;
  and_filters?: string[];
};

/* ------------------------------------------------------------------------- */
/* Schema — only the transforms                                               */
/* ------------------------------------------------------------------------- */

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const ExportFromApi = z.looseObject({
  id: z.number().transform(String),
  created_at: epochSecondsToDate,
  updated_at: epochSecondsToDate,
}) as unknown as ZodType<Export, unknown>;

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const exportEntity = defineEntity<Export, never, undefined, undefined, 'workspace'>({
  name: 'export',
  scope: 'workspace',
  pk: (item) => item.id,
  schema: { read: ExportFromApi },
});

export const exportKeys = exportEntity.keys;

export function parseExport(raw: ApiExport): Export {
  return exportEntity.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions (ported from the Ember  */
/* model's computed getters)                                                  */
/* ------------------------------------------------------------------------- */

/** Export types that can be linked back to the resource they were generated from. */
const KNOWN_EXPORT_TYPES = [
  'delivery_totals',
  'deliveries',
  'customers',
  'customer_joined_relationships',
  'segment_membership',
];

export function getExportProgress(exportItem: Export): number {
  const { done, total } = exportItem;
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

// Mirrors the Ember model's `@notEmpty('path')` — a finished export has a download path.
export function isExportComplete(exportItem: Export): boolean {
  return exportItem.path != null && exportItem.path.length > 0;
}

/**
 * "Exported by" label. User id "1" means the export was started from the CIO CLI
 * tool, shown generically as "Customer.io"; a missing user means a deleted user.
 */
export function getExportedBy(exportItem: Export): { label: string; isEmail: boolean } {
  const user = exportItem.user;
  if (user == null) {
    return { label: '(deleted user)', isEmail: false };
  }
  if (user.id === '1') {
    return { label: 'Customer.io', isEmail: false };
  }
  return { label: user.email, isEmail: true };
}

type ExportLink = { url: string | null; label: string };

type LinkTarget =
  | { kind: 'campaign' | 'newsletter' | 'segment'; id: number }
  | { kind: 'newsletter-list' | 'people' | 'deliveries' | 'drafts' };

// First defined id from a list of optional sources.
function firstId(...values: Array<number | null | undefined>): number | undefined {
  return values.find((value) => value != null) ?? undefined;
}

// A delivery_totals export links to its campaign, or its newsletter(s).
function resolveDeliveryTotalsTarget(options: ExportOptions): LinkTarget | null {
  const campaignId = firstId(options.campaign_ids?.[0], options.campaign, options.campaign_id);
  if (campaignId != null) {
    return { kind: 'campaign', id: campaignId };
  }
  const newsletterId = firstId(options.newsletter_ids?.[0], options.newsletter, options.newsletter_id);
  if (newsletterId == null) {
    return null;
  }
  const hasMultipleNewsletters = (options.newsletter_ids?.length ?? 0) > 1;
  return hasMultipleNewsletters ? { kind: 'newsletter-list' } : { kind: 'newsletter', id: newsletterId };
}

// Resolve which Journeys resource (if any) an export links back to.
function getLinkTarget(exportItem: Export, options: ExportOptions): LinkTarget | null {
  if (!KNOWN_EXPORT_TYPES.includes(exportItem.type)) {
    return null;
  }

  switch (exportItem.type) {
    case 'delivery_totals':
      return resolveDeliveryTotalsTarget(options);
    case 'deliveries':
      return options.and_filters?.includes('drafted') === true ? { kind: 'drafts' } : { kind: 'deliveries' };
    case 'customers':
    case 'customer_joined_relationships':
      // The combined people+objects CSV is effectively a people export, so it
      // links back to the people page (the standalone relationships export has no
      // overview and isn't a known type here).
      return { kind: 'people' };
    case 'segment_membership':
      return options.segment_id != null ? { kind: 'segment', id: options.segment_id } : null;
    default:
      return null;
  }
}

/**
 * Build the "See …" deep link for a completed export, pointing back at the
 * Journeys resource it was generated from. Ported from the Ember `getExportLink`
 * helper. When the export has filter conditions (`options.api_filters`), the value
 * is passed directly as `?filters=` — the backend stores it as a pre-encoded base64
 * string that Journeys decodes to restore the filtered view.
 */
export function getExportLink(exportItem: Export, workspaceId: number): ExportLink {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const options = (exportItem.options ?? {}) as ExportOptions;
  const target = getLinkTarget(exportItem, options);

  if (target == null) {
    return { url: null, label: 'View overview' };
  }

  switch (target.kind) {
    case 'campaign':
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
          workspaceId,
          campaignId: target.id,
        }),
        label: 'View overview',
      };
    case 'newsletter':
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId', {
          workspaceId,
          newsletterId: target.id,
        }),
        label: 'View overview',
      };
    case 'newsletter-list':
      return {
        url: generatePath(
          '/workspaces/:workspaceId/journeys/broadcasts/newsletters',
          { workspaceId },
          { type: 'sent' },
        ),
        label: 'View overview',
      };
    case 'people': {
      const hasFilters = options.api_filters != null;
      const query = hasFilters
        ? { filters: options.api_filters }
        : options.email != null
          ? { email: options.email }
          : undefined;
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/people', { workspaceId }, query),
        label: hasFilters || options.email != null ? 'View filters' : 'View overview',
      };
    }
    case 'segment':
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
          workspaceId,
          segmentId: target.id,
        }),
        label: 'View segment',
      };
    case 'deliveries':
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/outbox/deliveries', { workspaceId }),
        label: 'View overview',
      };
    case 'drafts':
      return {
        url: generatePath('/workspaces/:workspaceId/journeys/outbox/drafts', { workspaceId }),
        label: 'View overview',
      };
    default: {
      const _exhaustive: never = target;
      return { url: null, label: String(_exhaustive) };
    }
  }
}
